
import React, { useState, useMemo } from 'react';
import { Product, Order, OrderStatus } from '../types';
import { Trash2, BarChart3, Package, ShoppingCart, LogOut, Edit3, XCircle, ListChecks, CheckCircle2, AlertTriangle, RotateCcw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';

interface AdminProps {
  products: Product[];
  orders: Order[];
  setProducts: React.Dispatch<React.SetStateAction<Product[]>>;
  setOrders: React.Dispatch<React.SetStateAction<Order[]>>;
  onLogout: () => void;
  monthlyGoal: number;
  setMonthlyGoal: (goal: number) => void;
}

const PREDEFINED_LABELS = ['Aucun', 'Best-seller', 'Promo', 'Nouveau', 'Édition Limitée', 'Rupture'];
const INITIAL_CATEGORIES = ['Polène', 'Coach', 'Gucci', 'Artisanat', 'Chanel'];

const Admin: React.FC<AdminProps> = ({ products, orders, setProducts, setOrders, onLogout, monthlyGoal, setMonthlyGoal }) => {
  const [activeTab, setActiveTab] = useState<'dashboard' | 'products' | 'orders'>('dashboard');
  const [isEditing, setIsEditing] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isAddingNewCategory, setIsAddingNewCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const navigate = useNavigate();

  // Liste dynamique des catégories (basée sur les produits + initiales)
  const availableCategories = useMemo(() => {
    const fromProducts = products.map(p => p.category);
    return Array.from(new Set([...INITIAL_CATEGORIES, ...fromProducts])).sort();
  }, [products]);

  const totalRevenue = useMemo(() => {
    return orders
      .filter(order => order.status === 'livrée')
      .reduce((acc, curr) => acc + curr.total, 0);
  }, [orders]);
  
  const goalProgress = useMemo(() => {
    if (monthlyGoal === 0) return 0;
    return Math.round((totalRevenue / monthlyGoal) * 100);
  }, [totalRevenue, monthlyGoal]);

  const [formData, setFormData] = useState<Partial<Product>>({
    name: '', price: 0, category: 'Polène', label: 'Aucun', image: '', hoverImage: '', stock: 0, isOutOfStock: false
  });

  const [promoPriceInput, setPromoPriceInput] = useState<number | undefined>(undefined);

  const handleMassStockReset = async () => {
    if (!confirm("Voulez-vous remettre TOUS les stocks à 0 ? (Action irréversible)")) return;
    
    setIsSaving(true);
    try {
      const updatedProducts = products.map(p => ({ ...p, stock: 0, isOutOfStock: true }));
      for (const p of updatedProducts) {
        await supabase.from('products').update({ stock: 0, isOutOfStock: true }).eq('id', p.id);
      }
      setProducts(updatedProducts);
      alert("Tous les stocks ont été mis à 0.");
    } catch (err) {
      alert("Erreur lors de la mise à jour massive.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateGoal = async (newGoal: number) => {
    setMonthlyGoal(newGoal);
    await supabase.from('settings').update({ value: newGoal }).eq('key', 'monthly_goal');
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>, field: 'image' | 'hoverImage') => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setFormData(prev => ({ ...prev, [field]: reader.result as string }));
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const categoryToSave = isAddingNewCategory ? newCategoryName.trim() : formData.category;

    if (!formData.name || !formData.price || !formData.image || !categoryToSave) {
      alert("Informations manquantes. Assurez-vous d'avoir saisi le nom, le prix, l'image et la marque.");
      return;
    }

    setIsSaving(true);
    const stockToSave = Number(formData.stock) || 0;
    const basePrice = Number(formData.price) || 0;
    
    let finalPrice = basePrice;
    let finalOriginalPrice = null;

    if (formData.label === 'Promo' && promoPriceInput) {
      finalPrice = Number(promoPriceInput);
      finalOriginalPrice = basePrice;
    }

    const productPayload = {
      name: formData.name,
      price: finalPrice,
      originalPrice: finalOriginalPrice,
      category: categoryToSave,
      stock: stockToSave,
      label: formData.label === 'Aucun' ? null : formData.label,
      isOutOfStock: stockToSave <= 0,
      image: formData.image,
      hoverImage: formData.hoverImage || formData.image,
      description: formData.description || ''
    };

    try {
      if (isEditing) {
        const { error } = await supabase.from('products').update(productPayload).eq('id', isEditing);
        if (error) throw error;
        setProducts(prev => prev.map(p => p.id === isEditing ? { ...p, ...productPayload, id: p.id } as Product : p));
      } else {
        const newId = `kb-${Date.now()}`;
        const { error } = await supabase.from('products').insert([{ ...productPayload, id: newId }]);
        if (error) throw error;
        setProducts(prev => [{ ...productPayload, id: newId } as Product, ...prev]);
      }
      resetForm();
    } catch (err: any) {
      alert("Erreur lors de l'enregistrement : " + err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', price: 0, category: availableCategories[0], label: 'Aucun', image: '', hoverImage: '', stock: 0, isOutOfStock: false });
    setPromoPriceInput(undefined);
    setIsEditing(null);
    setIsAddingNewCategory(false);
    setNewCategoryName('');
  };

  const startEdit = (product: Product) => {
    setIsEditing(product.id);
    setFormData({
      ...product,
      label: product.label || 'Aucun',
      stock: Number(product.stock) || 0,
      price: product.originalPrice || product.price
    });
    if (product.originalPrice) setPromoPriceInput(product.price);
    setIsAddingNewCategory(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const updateOrderStatus = async (orderId: string, newStatus: OrderStatus) => {
    const order = orders.find(o => o.id === orderId);
    if (!order) return;
    
    const oldStatus = order.status;
    if (oldStatus === newStatus) return;

    let updatedProducts = [...products];

    if (newStatus === 'annulée' && oldStatus !== 'annulée') {
      for (const item of order.items) {
        const pIdx = updatedProducts.findIndex(p => p.id === item.id);
        if (pIdx > -1) {
          const p = updatedProducts[pIdx];
          const nStock = (Number(p.stock) || 0) + item.quantity;
          await supabase.from('products').update({ stock: nStock, isOutOfStock: nStock <= 0 }).eq('id', p.id);
          updatedProducts[pIdx] = { ...p, stock: nStock, isOutOfStock: nStock <= 0 };
        }
      }
    } 
    else if (oldStatus === 'annulée' && newStatus !== 'annulée') {
      for (const item of order.items) {
        const pIdx = updatedProducts.findIndex(p => p.id === item.id);
        if (pIdx > -1) {
          const p = updatedProducts[pIdx];
          const nStock = Math.max(0, (Number(p.stock) || 0) - item.quantity);
          await supabase.from('products').update({ stock: nStock, isOutOfStock: nStock <= 0 }).eq('id', p.id);
          updatedProducts[pIdx] = { ...p, stock: nStock, isOutOfStock: nStock <= 0 };
        }
      }
    }

    setProducts(updatedProducts);
    await supabase.from('orders').update({ status: newStatus }).eq('id', orderId);
    setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status: newStatus } : o));
  };

  const deleteOrder = async (id: string) => {
    const order = orders.find(o => o.id === id);
    if (!order) return;

    if (confirm("Supprimer cette commande ?")) {
      if (order.status !== 'annulée') {
        const updatedProducts = [...products];
        for (const item of order.items) {
          const pIdx = updatedProducts.findIndex(p => p.id === item.id);
          if (pIdx > -1) {
            const p = updatedProducts[pIdx];
            const nStock = (Number(p.stock) || 0) + item.quantity;
            await supabase.from('products').update({ stock: nStock, isOutOfStock: nStock <= 0 }).eq('id', p.id);
            updatedProducts[pIdx] = { ...p, stock: nStock, isOutOfStock: nStock <= 0 };
          }
        }
        setProducts(updatedProducts);
      }

      await supabase.from('orders').delete().eq('id', id);
      setOrders(prev => prev.filter(o => o.id !== id));
    }
  };

  const getStatusStyle = (status: OrderStatus) => {
    switch (status) {
      case 'en cours': return 'bg-amber-50 text-amber-600 border-amber-200';
      case 'livrée': return 'bg-emerald-50 text-emerald-600 border-emerald-200';
      case 'annulée': return 'bg-rose-50 text-rose-600 border-rose-200';
      default: return 'bg-gray-50 text-gray-600 border-gray-200';
    }
  };

  return (
    <div className="pt-24 min-h-screen bg-gray-50 pb-20 font-sans text-brand-black">
      <div className="container mx-auto px-6">
        <div className="flex justify-between items-center mb-10 border-b pb-6 border-gray-200">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="font-serif text-3xl text-brand-black">Console Directoire</h1>
            </div>
            <p className="text-[10px] text-gray-400 uppercase tracking-widest font-bold italic mt-1">Système Khamal Bags v2.1</p>
          </div>
          <button onClick={() => { onLogout(); navigate('/login'); }} className="text-[10px] uppercase tracking-widest font-bold text-gray-400 hover:text-red-500 luxury-transition flex items-center gap-2">
            <LogOut size={14} /> Déconnexion
          </button>
        </div>

        <div className="flex flex-col lg:flex-row gap-12">
          <div className="lg:w-64 flex flex-row lg:flex-col gap-2">
            {[
              { id: 'dashboard', icon: BarChart3, label: 'Pilotage' },
              { id: 'products', icon: Package, label: 'Catalogue' },
              { id: 'orders', icon: ShoppingCart, label: 'Commandes' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex-1 lg:flex-none flex items-center space-x-4 px-6 py-4 text-[10px] uppercase tracking-widest font-bold transition-all ${
                  activeTab === tab.id ? 'bg-brand-black text-white shadow-lg' : 'bg-white text-gray-400 border border-gray-100'
                }`}
              >
                <tab.icon size={16}/>
                <span>{tab.label}</span>
              </button>
            ))}
          </div>

          <div className="flex-1 space-y-8">
            {activeTab === 'dashboard' && (
              <div className="space-y-8 animate-in fade-in duration-500">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  <div className="bg-white p-8 border border-gray-100 flex flex-col justify-between h-48 shadow-sm">
                    <p className="text-[9px] uppercase tracking-widest text-gray-400 font-bold">CA Encaissé</p>
                    <p className="font-serif text-2xl text-brand-gold">{totalRevenue.toLocaleString()} FCFA</p>
                  </div>

                  <div className="bg-white p-8 border border-gray-100 flex flex-col justify-between h-48 shadow-sm">
                    <p className="text-[9px] uppercase tracking-widest text-gray-400 font-bold">Objectif</p>
                    <input 
                      type="number" 
                      value={monthlyGoal} 
                      onChange={e => handleUpdateGoal(Number(e.target.value))} 
                      className="w-full font-serif text-xl outline-none bg-transparent border-b border-gray-100 text-brand-black font-bold" 
                    />
                    <div className="w-full bg-gray-100 h-1 rounded-full overflow-hidden">
                      <div className="bg-brand-gold h-full" style={{width: `${Math.min(100, goalProgress)}%`}}></div>
                    </div>
                  </div>

                  <div className="bg-white p-8 border border-gray-100 flex flex-col justify-between h-48 shadow-sm">
                    <p className="text-[9px] uppercase tracking-widest text-gray-400 font-bold">Stock Global</p>
                    <div className="font-serif text-2xl font-bold">
                      {products.reduce((acc, p) => acc + (Number(p.stock) || 0), 0)} unités
                    </div>
                    <button 
                      onClick={handleMassStockReset} 
                      className="bg-red-50 text-red-600 text-[8px] uppercase py-2 font-bold hover:bg-red-600 hover:text-white transition-all flex items-center justify-center gap-2"
                    >
                      <RotateCcw size={10} /> Tout mettre à 0
                    </button>
                  </div>
                </div>

                <div className="bg-white p-8 border border-gray-100 shadow-sm">
                  <div className="flex items-center gap-3 mb-8">
                    <ListChecks className="text-brand-gold" size={20} />
                    <h3 className="font-serif text-xl">État de l'Inventaire</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-left">
                      <thead>
                        <tr className="border-b border-gray-100">
                          <th className="pb-4 text-[10px] uppercase tracking-widest font-bold text-gray-400">Article</th>
                          <th className="pb-4 text-[10px] uppercase tracking-widest font-bold text-gray-400 text-center">Stock</th>
                          <th className="pb-4 text-[10px] uppercase tracking-widest font-bold text-gray-400 text-right">Statut</th>
                        </tr>
                      </thead>
                      <tbody>
                        {products.map(p => {
                          const s = Number(p.stock) || 0;
                          return (
                            <tr key={p.id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50 transition-colors">
                              <td className="py-5 flex items-center gap-4">
                                <img src={p.image} className="w-8 h-10 object-cover" alt=""/>
                                <span className={`text-sm font-medium ${s <= 0 ? 'text-gray-400' : 'text-brand-black'}`}>{p.name}</span>
                              </td>
                              <td className="py-5 text-center font-bold">
                                <span className={`px-4 py-1 rounded-sm text-xs ${s <= 0 ? 'text-red-600 bg-red-50' : 'text-green-700 bg-green-50'}`}>
                                  {s}
                                </span>
                              </td>
                              <td className="py-5 text-right">
                                 {s <= 0 ? (
                                   <span className="text-[9px] font-bold text-red-600 uppercase tracking-widest flex items-center justify-end gap-1"><AlertTriangle size={12}/> ÉPUISÉ</span>
                                 ) : (
                                   <span className="text-[9px] font-bold text-green-600 uppercase tracking-widest flex items-center justify-end gap-1"><CheckCircle2 size={12}/> EN STOCK</span>
                                 )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'products' && (
              <div className="space-y-12 animate-in fade-in duration-500">
                <form onSubmit={handleSave} className="bg-white p-10 border border-gray-200 shadow-xl relative">
                  {isEditing && (
                    <button type="button" onClick={resetForm} className="absolute top-6 right-6 text-gray-400 hover:text-red-500 flex items-center gap-1 text-[9px] uppercase font-bold">
                      <XCircle size={16} /> Annuler
                    </button>
                  )}
                  
                  <h3 className="font-serif text-2xl mb-10 text-brand-gold">
                    {isEditing ? 'Éditer le sac' : 'Nouveau Catalogue'}
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                    <div className="space-y-6">
                      <div>
                        <label className="text-[10px] uppercase tracking-widest font-bold text-brand-black block mb-2">Désignation</label>
                        <input className="w-full p-4 border border-gray-200 bg-gray-50 text-sm outline-none focus:border-brand-gold" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} />
                      </div>
                      
                      <div className="grid grid-cols-2 gap-6">
                        <div>
                          <label className="text-[10px] uppercase tracking-widest font-bold text-brand-black block mb-2">Prix (FCFA)</label>
                          <input type="number" className="w-full p-4 border border-gray-200 bg-gray-50 text-sm font-bold" value={formData.price || ''} onChange={e => setFormData({...formData, price: Number(e.target.value)})} />
                        </div>
                        <div>
                          <label className="text-[10px] uppercase tracking-widest font-bold text-brand-black block mb-2 italic text-brand-gold">Stock Actuel</label>
                          <input 
                            type="number" 
                            className="w-full p-4 border-2 border-brand-gold bg-white text-sm font-bold text-brand-black outline-none shadow-md" 
                            value={formData.stock} 
                            onChange={e => {
                                const val = e.target.value;
                                setFormData(prev => ({...prev, stock: val === '' ? 0 : Number(val)}));
                            }} 
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-6">
                        <div>
                          <label className="text-[10px] uppercase tracking-widest font-bold text-brand-black block mb-2">Tag</label>
                          <select className="w-full p-4 border border-gray-200 bg-gray-50 text-xs font-bold" value={formData.label || 'Aucun'} onChange={e => setFormData({...formData, label: e.target.value})}>
                            {PREDEFINED_LABELS.map(l => <option key={l} value={l}>{l}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="text-[10px] uppercase tracking-widest font-bold text-brand-black block mb-2">Maison / Marque</label>
                          <select 
                            className={`w-full p-4 border text-xs font-bold luxury-transition ${isAddingNewCategory ? 'border-brand-gold bg-brand-cream/30' : 'border-gray-200 bg-gray-50'}`}
                            value={isAddingNewCategory ? 'NEW' : formData.category} 
                            onChange={e => {
                              if (e.target.value === 'NEW') {
                                setIsAddingNewCategory(true);
                              } else {
                                setIsAddingNewCategory(false);
                                setFormData({...formData, category: e.target.value});
                              }
                            }}
                          >
                            {availableCategories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                            <option value="NEW" className="text-brand-gold font-bold">+ Ajouter une nouvelle marque</option>
                          </select>
                          
                          {isAddingNewCategory && (
                            <div className="mt-3 animate-in fade-in slide-in-from-top-2 duration-300">
                              <input 
                                type="text"
                                placeholder="Nom de la nouvelle maison"
                                className="w-full p-3 border-2 border-brand-gold bg-white text-xs font-bold outline-none shadow-sm"
                                value={newCategoryName}
                                onChange={e => setNewCategoryName(e.target.value)}
                                autoFocus
                              />
                            </div>
                          )}
                        </div>
                      </div>

                      {formData.label === 'Promo' && (
                        <div className="bg-red-50 p-6 border border-red-100">
                          <label className="text-[10px] uppercase tracking-widest font-bold text-red-600 block mb-2">Prix Remisé</label>
                          <input type="number" className="w-full p-4 border border-red-200 bg-white text-sm font-bold text-red-600 outline-none" value={promoPriceInput || ''} onChange={e => setPromoPriceInput(Number(e.target.value))} />
                        </div>
                      )}
                    </div>

                    <div className="space-y-6">
                      <div className="bg-brand-cream/50 p-6 border border-dashed border-brand-gold/30">
                        <h4 className="text-[10px] uppercase font-bold text-brand-gold mb-4 text-center">Images</h4>
                        <div className="grid grid-cols-2 gap-4">
                          <label className="flex flex-col items-center justify-center border-2 border-dashed border-gray-200 aspect-[3/4] cursor-pointer hover:bg-white transition-all overflow-hidden relative bg-white">
                            {formData.image ? <img src={formData.image} className="w-full h-full object-cover" alt=""/> : <span className="text-[8px] text-gray-400 font-bold">FACE</span>}
                            <input type="file" className="hidden" accept="image/*" onChange={e => handleImageUpload(e, 'image')} />
                          </label>
                          <label className="flex flex-col items-center justify-center border-2 border-dashed border-gray-200 aspect-[3/4] cursor-pointer hover:bg-white transition-all overflow-hidden relative bg-white">
                            {formData.hoverImage ? <img src={formData.hoverImage} className="w-full h-full object-cover" alt=""/> : <span className="text-[8px] text-gray-400 font-bold">REVERS</span>}
                            <input type="file" className="hidden" accept="image/*" onChange={e => handleImageUpload(e, 'hoverImage')} />
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>

                  <button 
                    type="submit" 
                    disabled={isSaving}
                    className={`w-full bg-brand-black text-white py-5 text-[11px] uppercase tracking-[0.3em] font-bold hover:bg-brand-gold transition-all shadow-xl mt-10 ${isSaving ? 'opacity-50' : ''}`}
                  >
                    {isSaving ? 'Traitement...' : isEditing ? 'Valider les changements' : 'Ajouter l\'article'}
                  </button>
                </form>

                <div className="space-y-4">
                    <p className="text-[10px] uppercase tracking-widest font-bold text-gray-400">Liste des Produits</p>
                    <div className="grid grid-cols-1 gap-3">
                      {products.map(p => {
                        const sNum = Number(p.stock) || 0;
                        return (
                          <div key={p.id} className="bg-white p-4 border border-gray-100 flex items-center justify-between group hover:border-brand-gold luxury-transition shadow-sm">
                            <div className="flex items-center gap-6">
                              <img src={p.image} className={`w-12 h-16 object-cover shadow-sm ${sNum <= 0 ? 'opacity-50' : ''}`} alt=""/>
                              <div>
                                <h5 className={`font-serif text-lg ${sNum <= 0 ? 'text-gray-400' : 'text-brand-black'}`}>{p.name}</h5>
                                <div className="flex items-center gap-4 mt-1">
                                  <span className="text-[10px] font-bold text-brand-gold">{p.price.toLocaleString()} FCFA</span>
                                  <span className={`text-[9px] font-bold px-2 py-0.5 rounded-sm ${sNum > 0 ? 'bg-green-50 text-green-600' : 'bg-red-600 text-white'}`}>
                                    STOCK: {sNum}
                                  </span>
                                  <span className="text-[8px] uppercase font-bold text-gray-300 ml-2 italic">Maison {p.category}</span>
                                  {p.label && (
                                    <span className="text-[8px] uppercase font-bold text-gray-400 border px-1.5 py-0.5 ml-2">{p.label}</span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <button onClick={() => startEdit(p)} className="p-2 text-gray-400 hover:text-brand-gold transition-colors"><Edit3 size={18} /></button>
                              <button onClick={async () => { if(confirm("Supprimer ?")) { await supabase.from('products').delete().eq('id', p.id); setProducts(prev => prev.filter(item => item.id !== p.id)); } }} className="p-2 text-gray-200 hover:text-red-500 transition-colors"><Trash2 size={18} /></button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                </div>
              </div>
            )}
            
            {activeTab === 'orders' && (
               <div className="space-y-8 animate-in fade-in duration-500">
                 {orders.map(o => (
                   <div key={o.id} className="bg-white p-8 border border-gray-100 shadow-sm hover:border-brand-gold luxury-transition">
                      <div className="flex flex-col md:flex-row justify-between gap-6 mb-8 pb-6 border-b border-gray-50">
                        <div>
                          <div className="flex items-center gap-3 mb-2">
                            <p className="text-[10px] font-bold uppercase tracking-widest text-brand-gold">COMMANDE #{o.id.slice(-6)}</p>
                            <span className={`text-[8px] uppercase font-bold px-3 py-1 border rounded-full ${getStatusStyle(o.status)}`}>
                              {o.status}
                            </span>
                          </div>
                          <h4 className="font-serif text-2xl mb-1">{o.customerName}</h4>
                          <p className="text-[10px] text-gray-400 font-medium tracking-wider">{o.customerPhone}</p>
                          <p className="text-[9px] text-brand-gold font-bold uppercase tracking-widest mt-2">Via {o.paymentMethod}</p>
                        </div>
                        <div className="text-right">
                           <p className="font-serif text-2xl text-brand-black">{o.total.toLocaleString()} FCFA</p>
                           <p className="text-[9px] text-gray-400 uppercase tracking-widest mt-2">{o.items.reduce((acc, i) => acc + i.quantity, 0)} article(s) total</p>
                        </div>
                      </div>

                      <div className="mb-8 space-y-4">
                        <p className="text-[9px] uppercase tracking-widest font-bold text-gray-400 mb-2">Contenu du panier</p>
                        <div className="grid grid-cols-1 gap-4">
                          {o.items.map((item, idx) => (
                            <div key={`${o.id}-item-${idx}`} className="flex items-center justify-between border-b border-gray-50 pb-4 last:border-0">
                              <div className="flex items-center gap-4">
                                <img 
                                  src={item.selectedVariant?.image || item.image} 
                                  className="w-10 h-12 object-cover border border-gray-50 shadow-sm" 
                                  alt=""
                                />
                                <div>
                                  <p className="text-xs font-bold text-brand-black">{item.name}</p>
                                  {item.selectedVariant && (
                                    <p className="text-[8px] uppercase tracking-widest text-brand-gold font-bold">Variante: {item.selectedVariant.name}</p>
                                  )}
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="text-xs font-bold text-brand-black">x{item.quantity}</p>
                                <p className="text-[9px] text-gray-400">{(item.price * item.quantity).toLocaleString()} FCFA</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center justify-between gap-4">
                         <div className="flex items-center gap-2">
                           {(['en cours', 'livrée', 'annulée'] as OrderStatus[]).map(s => (
                             <button 
                               key={s} 
                               onClick={() => updateOrderStatus(o.id, s)} 
                               className={`text-[8px] uppercase font-bold px-3 py-1.5 transition-all ${o.status === s ? 'bg-brand-black text-white shadow-md' : 'bg-white text-gray-400 border border-gray-100 hover:border-brand-gold'}`}
                             >
                               {s}
                             </button>
                           ))}
                         </div>
                         <button onClick={() => deleteOrder(o.id)} className="text-red-200 hover:text-red-500 luxury-transition"><Trash2 size={16} /></button>
                      </div>
                   </div>
                 ))}
                 {orders.length === 0 && (
                   <div className="text-center py-20 text-gray-400 font-serif italic">Aucune commande enregistrée.</div>
                 )}
               </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Admin;
